// Per-dialect synthesizer for engine-specific table options (v2 Slice E3).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice E3.
//
// Per-engine surface:
//   - mssql:  ALTER TABLE … REBUILD WITH (DATA_COMPRESSION = …)  (compression)
//             ALTER TABLE … SET (DURABILITY = …, SYSTEM_VERSIONING = …)
//             Multi-statement when both compression and SET-shaped options
//             are requested in a single change (joined with `;\n`).
//   - pgsql:  ALTER TABLE … SET (key = value, …)
//             ALTER TABLE … RESET (key, …)
//             Multi-statement when both set + reset lists are present.
//   - mysql:  ALTER TABLE … <opt = val>, … (single statement combining every
//             field set).
//   - oracle: ALTER TABLE … <PCTFREE n> <PCTUSED n> <INITRANS n> <MAXTRANS n>
//             <STORAGE (…)> <LOGGING|NOLOGGING> <compress-text> (single
//             statement; clauses appear in source order — see the field
//             order reflected in the synth, which mirrors the Oracle SQL
//             Reference's grammar order).
//   - sqlite: rejects (WITHOUT ROWID / STRICT are CREATE TABLE attributes).

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { TableOptionsChange } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';

function tableText(engine: SqlEngine, table: QualifiedName): string {
  if (table.raw && table.raw.length > 0) return table.raw;
  const name = maybeQuoteIdent(engine, table.name, table.quoted);
  if (table.schema) {
    const schema = maybeQuoteIdent(engine, table.schema, table.quoted);
    return `${schema}.${name}`;
  }
  return name;
}

function rejectField(
  engine: SqlEngine,
  field: string,
  allowed: string,
): never {
  throw new Error(
    `applyDdlEdit: ${engine} alter-table-options does not accept '${field}' `
      + `— only ${allowed} accept this field.`,
  );
}

function hasMssqlField(c: TableOptionsChange): boolean {
  return (
    c.mssqlDataCompression !== undefined
    || c.mssqlDurability !== undefined
    || c.mssqlSystemVersioning !== undefined
  );
}

function hasPgsqlField(c: TableOptionsChange): boolean {
  return c.pgsqlSetOptions !== undefined || c.pgsqlResetOptions !== undefined;
}

function hasMysqlField(c: TableOptionsChange): boolean {
  return (
    c.mysqlEngine !== undefined
    || c.mysqlDefaultCharset !== undefined
    || c.mysqlCollate !== undefined
    || c.mysqlRowFormat !== undefined
    || c.mysqlKeyBlockSize !== undefined
    || c.mysqlStatsPersistent !== undefined
  );
}

function hasOracleField(c: TableOptionsChange): boolean {
  return (
    c.oraclePctfree !== undefined
    || c.oraclePctused !== undefined
    || c.oracleInitrans !== undefined
    || c.oracleMaxtrans !== undefined
    || c.oracleStorageText !== undefined
    || c.oracleLogging !== undefined
    || c.oracleCompressText !== undefined
  );
}

function hasAnyField(c: TableOptionsChange): boolean {
  return (
    hasMssqlField(c) || hasPgsqlField(c) || hasMysqlField(c) || hasOracleField(c)
  );
}

export function synthesizeAlterTableOptions(
  engine: SqlEngine,
  table: QualifiedName,
  change: TableOptionsChange,
): string {
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: sqlite does not support alter-table-options — sqlite's `
        + `WITHOUT ROWID and STRICT attributes are CREATE TABLE only and `
        + `cannot be ALTERed. Rebuild the table manually with the desired `
        + `attribute (CREATE TABLE, copy the data across, DROP the old table, `
        + `RENAME the new one) — the opt-in 'sqliteRecreate' path only `
        + `rewrites columns (add/drop/rename/alter), not table attributes.`,
    );
  }
  if (!hasAnyField(change)) {
    throw new Error(
      `applyDdlEdit: alter-table-options requires at least one change field.`,
    );
  }
  if (engine === 'mssql') return synthMssql(table, change);
  if (engine === 'postgres') return synthPgsql(table, change);
  if (engine === 'mysql') return synthMysql(table, change);
  return synthOracle(table, change);
}

// ─── mssql ────────────────────────────────────────────────────────────────

function synthMssql(table: QualifiedName, c: TableOptionsChange): string {
  if (hasPgsqlField(c)) rejectField('mssql', 'pgsql*', 'pgsql');
  if (hasMysqlField(c)) rejectField('mssql', 'mysql*', 'mysql');
  if (hasOracleField(c)) rejectField('mssql', 'oracle*', 'oracle');
  const tbl = tableText('mssql', table);
  const setEntries: string[] = [];
  if (c.mssqlDurability !== undefined) {
    setEntries.push(`DURABILITY = ${formatDurability(c.mssqlDurability)}`);
  }
  if (c.mssqlSystemVersioning !== undefined) {
    setEntries.push(formatSystemVersioning(c.mssqlSystemVersioning));
  }
  const stmts: string[] = [];
  if (setEntries.length > 0) {
    stmts.push(`ALTER TABLE ${tbl} SET (${setEntries.join(', ')})`);
  }
  if (c.mssqlDataCompression !== undefined) {
    stmts.push(
      `ALTER TABLE ${tbl} REBUILD WITH `
        + `(DATA_COMPRESSION = ${c.mssqlDataCompression.toUpperCase()})`,
    );
  }
  return stmts.join(';\n');
}

function formatDurability(d: 'schema-and-data' | 'schema-only'): string {
  return d === 'schema-and-data' ? 'SCHEMA_AND_DATA' : 'SCHEMA_ONLY';
}

function formatSystemVersioning(
  spec: { readonly enabled: false } | { readonly enabled: true; readonly historyTable?: string },
): string {
  if (!spec.enabled) return 'SYSTEM_VERSIONING = OFF';
  if (spec.historyTable !== undefined) {
    return `SYSTEM_VERSIONING = ON (HISTORY_TABLE = ${spec.historyTable})`;
  }
  return 'SYSTEM_VERSIONING = ON';
}

// ─── pgsql ────────────────────────────────────────────────────────────────

function synthPgsql(table: QualifiedName, c: TableOptionsChange): string {
  if (hasMssqlField(c)) rejectField('postgres', 'mssql*', 'mssql');
  if (hasMysqlField(c)) rejectField('postgres', 'mysql*', 'mysql');
  if (hasOracleField(c)) rejectField('postgres', 'oracle*', 'oracle');
  const tbl = tableText('postgres', table);
  const stmts: string[] = [];
  if (c.pgsqlSetOptions !== undefined && c.pgsqlSetOptions.length > 0) {
    stmts.push(`ALTER TABLE ${tbl} SET (${c.pgsqlSetOptions.join(', ')})`);
  }
  if (c.pgsqlResetOptions !== undefined && c.pgsqlResetOptions.length > 0) {
    stmts.push(`ALTER TABLE ${tbl} RESET (${c.pgsqlResetOptions.join(', ')})`);
  }
  if (stmts.length === 0) {
    throw new Error(
      `applyDdlEdit: postgres alter-table-options requires at least one of `
        + `pgsqlSetOptions / pgsqlResetOptions to be a non-empty list.`,
    );
  }
  return stmts.join(';\n');
}

// ─── mysql ────────────────────────────────────────────────────────────────

function synthMysql(table: QualifiedName, c: TableOptionsChange): string {
  if (hasMssqlField(c)) rejectField('mysql', 'mssql*', 'mssql');
  if (hasPgsqlField(c)) rejectField('mysql', 'pgsql*', 'pgsql');
  if (hasOracleField(c)) rejectField('mysql', 'oracle*', 'oracle');
  const tbl = tableText('mysql', table);
  const parts: string[] = [];
  if (c.mysqlEngine !== undefined) parts.push(`ENGINE = ${c.mysqlEngine}`);
  if (c.mysqlDefaultCharset !== undefined) {
    parts.push(`DEFAULT CHARSET = ${c.mysqlDefaultCharset}`);
  }
  if (c.mysqlCollate !== undefined) parts.push(`COLLATE = ${c.mysqlCollate}`);
  if (c.mysqlRowFormat !== undefined) {
    parts.push(`ROW_FORMAT = ${c.mysqlRowFormat.toUpperCase()}`);
  }
  if (c.mysqlKeyBlockSize !== undefined) {
    parts.push(`KEY_BLOCK_SIZE = ${c.mysqlKeyBlockSize}`);
  }
  if (c.mysqlStatsPersistent !== undefined) {
    const v = c.mysqlStatsPersistent === 'default'
      ? 'DEFAULT'
      : String(c.mysqlStatsPersistent);
    parts.push(`STATS_PERSISTENT = ${v}`);
  }
  return `ALTER TABLE ${tbl} ${parts.join(', ')}`;
}

// ─── oracle ───────────────────────────────────────────────────────────────

function synthOracle(table: QualifiedName, c: TableOptionsChange): string {
  if (hasMssqlField(c)) rejectField('oracle', 'mssql*', 'mssql');
  if (hasPgsqlField(c)) rejectField('oracle', 'pgsql*', 'pgsql');
  if (hasMysqlField(c)) rejectField('oracle', 'mysql*', 'mysql');
  const tbl = tableText('oracle', table);
  const parts: string[] = [];
  if (c.oraclePctfree !== undefined) parts.push(`PCTFREE ${c.oraclePctfree}`);
  if (c.oraclePctused !== undefined) parts.push(`PCTUSED ${c.oraclePctused}`);
  if (c.oracleInitrans !== undefined) parts.push(`INITRANS ${c.oracleInitrans}`);
  if (c.oracleMaxtrans !== undefined) parts.push(`MAXTRANS ${c.oracleMaxtrans}`);
  if (c.oracleStorageText !== undefined) {
    parts.push(`STORAGE (${c.oracleStorageText})`);
  }
  if (c.oracleLogging !== undefined) {
    parts.push(c.oracleLogging.toUpperCase());
  }
  if (c.oracleCompressText !== undefined) parts.push(c.oracleCompressText);
  return `ALTER TABLE ${tbl} ${parts.join(' ')}`;
}
